/* eslint-disable unicorn/filename-case */
import { useCallback, useEffect, useRef, useState } from "react";

import { generationModes } from "shared/constants";
import { noop } from "shared/functions";
import { useGlobalsContext } from "shared/hooks";
import { getIpLookup } from "shared/ipLookup";
import { AppConfiguration, GenerationMode } from "shared/types";
import { removePeriodsAfterURL } from "shared/utils/sourceUtils";

function useConnectWebsocket(
  botId: string,
  config: AppConfiguration,
  supportEmail: string | null,
  chatId: string,
  setGenerationMode: React.Dispatch<React.SetStateAction<GenerationMode>>,
  setChatData: React.Dispatch<React.SetStateAction<any[]>>,
  setMessage: React.Dispatch<React.SetStateAction<string>>,
  useWebsocketGeneration: boolean,
  setUseWebsocketGeneration: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const { getParentUrl } = useGlobalsContext();
  const [ipAddress, setIpAddress] = useState<string>();

  const [ws, setWs] = useState<WebSocket | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    getIpLookup({})
      .then(data => setIpAddress(data?.ip))
      .catch(noop);
  }, []);

  const updateEventHandler = useCallback(
    ({ data }: { data: string }) => {
      setChatData(prevChatData => {
        const chatArray = [...prevChatData];
        const lastMessage = chatArray.pop();
        chatArray.push(lastMessage, {
          message: data,
          sent: false,
          showLikeDislikeBtn: false,
        });
        return chatArray;
      });
      setGenerationMode(null);
    },
    [setChatData, setGenerationMode],
  );

  const ticketSolvedEventHandler = useCallback(
    ({ data }: { data: string }) => {
      setUseWebsocketGeneration(false);
      setChatData(prevChatData => {
        const chatArray = [...prevChatData];
        const lastMessage = chatArray.pop();
        chatArray.push(lastMessage, {
          message: data,
          sent: false,
          showLikeDislikeBtn: false,
        });
        return chatArray;
      });
      setGenerationMode(null);
    },
    [setChatData, setGenerationMode, setUseWebsocketGeneration],
  );

  const endEventHandler = useCallback(() => {
    setChatData(prevChatData => {
      const chatArray = [...prevChatData];
      const lastMessage = chatArray.pop();

      chatArray.push({
        ...lastMessage,
        message: removePeriodsAfterURL(lastMessage?.message || ""),
        sent: false,
        showLikeDislikeBtn: false,
      });
      return chatArray;
    });

    setGenerationMode(null);
  }, [setChatData, setGenerationMode]);

  const messageIdHandler = useCallback(
    (id: string) => {
      setChatData(prevChatData => {
        const chatArray = [...prevChatData];
        const lastMessage = chatArray.pop();
        chatArray.push({
          ...lastMessage,
          messageId: id,
        });
        return chatArray;
      });
    },
    [setChatData],
  );

  const errorEventHandler = useCallback(() => {
    setChatData(prevChatData => {
      const chatArray = [...prevChatData];
      const message = `Something went wrong. Please get in touch with support${
        supportEmail ? " at " + supportEmail : ""
      }.`;

      chatArray.push({
        message,
        sent: false,
      });
      return chatArray;
    });

    setGenerationMode(null);
  }, [setChatData, setGenerationMode, supportEmail]);

  const onMessageHandler = useCallback(
    (e: MessageEvent) => {
      if (!useWebsocketGeneration || !e.data) {
        return;
      }
      const event = JSON.parse(e.data);
      if (!!!event) {
        return;
      }
      switch (event.event) {
        case "update": {
          return updateEventHandler({ data: event.data });
          break;
        }
        case "ticket_solved": {
          ticketSolvedEventHandler({ data: event.data });
          break;
        }
        case "message_id": {
          messageIdHandler(event.data);
          break;
        }
        case "error": {
          errorEventHandler();
          break;
        }
        case "end": {
          endEventHandler();
          break;
        }
      }
    },
    [
      endEventHandler,
      errorEventHandler,
      messageIdHandler,
      ticketSolvedEventHandler,
      updateEventHandler,
      useWebsocketGeneration,
    ],
  );

  const connectWebSocket = useCallback(() => {
    const websocketToken = process.env.REACT_APP_WEBSOCKET_TOKEN;
    if (!websocketToken || !config.token || !chatId) {
      return;
    }
    setWs(
      new WebSocket(
        `${websocketToken}?token=${config.token}&chat_id=${chatId}`,
      ),
    );
  }, [chatId, config.token]);

  const closeConnection = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(); // Close the current connection
      wsRef.current = null; // Reset the reference
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
    }
  }, []);

  useEffect(() => {
    connectWebSocket();
    return () => {
      closeConnection();
    };
  }, [closeConnection, connectWebSocket]);

  useEffect(() => {
    ws?.addEventListener("open", () => {
      // Clear any existing keep-alive interval and set a new one
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
      }
      keepAliveIntervalRef.current = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: "default" }));
        }
      }, 300_000); // Send ping every 5 minutes to keep the connection alive
    });

    ws?.addEventListener("close", () => {
      // Attempt to reconnect after a delay
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 2_000); // Reconnect after 2 seconds
    });

    ws?.addEventListener("error", e => {
      // eslint-disable-next-line no-console
      console.error("WebSocket error", e);
    });

    ws?.addEventListener("message", e => {
      onMessageHandler(e);
    });

    wsRef.current = ws;

    return () => {
      ws?.removeEventListener("open", () => {
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
        }
      });
      ws?.removeEventListener("close", () => {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      });
      ws?.removeEventListener("error", noop);
      ws?.removeEventListener("message", onMessageHandler);
    };
  }, [connectWebSocket, onMessageHandler, ws]);

  const sendWebsocketMessage = useCallback(
    (msg: string, chatUserId?: string): Promise<void> => {
      return new Promise(async (resolve, reject) => {
        if (!botId) {
          reject(new Error("Bot ID is required"));
          return;
        }
        setGenerationMode(generationModes.submitting);

        const parentUrlHref = await new Promise(_resolve =>
          getParentUrl(parentUrl =>
            _resolve(parentUrl ? parentUrl.href : window.location.href),
          ),
        );

        try {
          const params = {
            action: "sendMessage",
            data: {
              input_text: msg,
              token: config.token,
              chat_id: chatId,
              ip_address: ipAddress,
              chat_user_id: chatUserId,
              BaseOrigin: config.baseOrigin,
              page: parentUrlHref,
            },
          };

          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(params));
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              chatArray.push({
                message: msg,
                sent: true,
              });
              return chatArray;
            });
            setGenerationMode(null);
            setMessage("");
            resolve();
          } else {
            reject(new Error("WebSocket is not active"));
          }
        } catch (err) {
          reject(err);
        }
      });
    },
    [
      botId,
      chatId,
      config.baseOrigin,
      config.token,
      getParentUrl,
      ipAddress,
      setChatData,
      setGenerationMode,
      setMessage,
    ],
  );

  return {
    sendWebsocketMessage,
  };
}

export default useConnectWebsocket;
